/**
 * Header, Navigation, Predictive Search, and Cart Count Controller
 * Built without third-party frameworks for modern Shopify Online Store 2.0.
 */

document.addEventListener('DOMContentLoaded', () => {
  initAnnouncementBar();
  initStickyHeader();
  initMobileDrawer();
  initPredictiveSearch();
  initDesktopNavigation();
  initCartListener();
});

/* -----------------------------------------------------------------------------
   1. Announcement Bar
   ----------------------------------------------------------------------------- */
function initAnnouncementBar() {
  const bar = document.querySelector('[data-announcement-bar]');
  if (!bar) return;

  const count = parseInt(bar.dataset.count, 10);
  if (count <= 1) return;

  const messages = bar.querySelectorAll('[data-announcement-index]');
  const prevBtn = bar.querySelector('[data-announcement-prev]');
  const nextBtn = bar.querySelector('[data-announcement-next]');
  let currentIndex = 0;

  function showIndex(newIndex) {
    if (newIndex < 0) newIndex = count - 1;
    if (newIndex >= count) newIndex = 0;
    currentIndex = newIndex;

    messages.forEach((msg, idx) => {
      if (idx === currentIndex) {
        msg.hidden = false;
        msg.classList.add('announcement-bar__message--active');
      } else {
        msg.hidden = true;
        msg.classList.remove('announcement-bar__message--active');
      }
    });
  }

  if (prevBtn) {
    prevBtn.addEventListener('click', () => showIndex(currentIndex - 1));
  }
  if (nextBtn) {
    nextBtn.addEventListener('click', () => showIndex(currentIndex + 1));
  }
}

/* -----------------------------------------------------------------------------
   2. Sticky Header
   ----------------------------------------------------------------------------- */
function initStickyHeader() {
  const header = document.querySelector('[data-header]');
  if (!header) return;

  const stickyMode = header.dataset.sticky;
  if (stickyMode !== 'on_scroll_up') return;

  let lastScrollY = window.scrollY;
  let ticking = false;

  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;

          // Don't hide if drawer or search modal is open
          const modalOpen = document.querySelector('.search-modal:not([hidden]), .mobile-drawer:not([hidden])');
          if (modalOpen) {
            lastScrollY = currentScrollY;
            ticking = false;
            return;
          }

          if (currentScrollY > 120 && currentScrollY > lastScrollY) {
            // Scrolling down -> hide header
            header.classList.add('header--hidden');
          } else if (currentScrollY < lastScrollY) {
            // Scrolling up -> reveal header
            header.classList.remove('header--hidden');
          }

          if (currentScrollY > 20) {
            header.classList.add('header--scrolled');
          } else {
            header.classList.remove('header--scrolled');
            header.classList.remove('header--hidden');
          }

          lastScrollY = currentScrollY <= 0 ? 0 : currentScrollY;
          ticking = false;
        });
        ticking = true;
      }
    },
    { passive: true }
  );
}

/* -----------------------------------------------------------------------------
   3. Mobile Navigation Drawer
   ----------------------------------------------------------------------------- */
function initMobileDrawer() {
  const drawer = document.getElementById('mobile-navigation-drawer');
  const trigger = document.querySelector('[data-drawer-trigger="mobile-nav"]');
  if (!drawer || !trigger) return;

  const closeBtns = drawer.querySelectorAll('[data-close-drawer]');
  const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  function openDrawer() {
    drawer.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    document.documentElement.setAttribute('scroll-lock', '');
    document.body.style.overflow = 'hidden';

    // Focus close button
    const closeBtn = drawer.querySelector('.mobile-drawer__close');
    if (closeBtn) closeBtn.focus();

    document.addEventListener('keydown', handleKeydown);
  }

  function closeDrawer() {
    drawer.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    document.documentElement.removeAttribute('scroll-lock');
    document.body.style.overflow = '';

    trigger.focus();
    document.removeEventListener('keydown', handleKeydown);
  }

  function handleKeydown(event) {
    if (event.key === 'Escape') {
      closeDrawer();
      return;
    }

    // Trap focus inside drawer
    if (event.key === 'Tab') {
      const focusableElements = drawer.querySelectorAll(focusableSelector);
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  trigger.addEventListener('click', openDrawer);
  closeBtns.forEach((btn) => btn.addEventListener('click', closeDrawer));
}

/* -----------------------------------------------------------------------------
   4. Desktop Navigation Keyboard Accessibility
   ----------------------------------------------------------------------------- */
function initDesktopNavigation() {
  const navItems = document.querySelectorAll('.header__nav-item--has-dropdown');

  navItems.forEach((item) => {
    const link = item.querySelector('.header__nav-link');
    const dropdown = item.querySelector('.header__dropdown, .header__mega-menu');
    if (!link || !dropdown) return;

    // Toggle aria-expanded on focus/blur
    item.addEventListener('focusin', () => {
      link.setAttribute('aria-expanded', 'true');
    });

    item.addEventListener('focusout', (e) => {
      if (!item.contains(e.relatedTarget)) {
        link.setAttribute('aria-expanded', 'false');
      }
    });

    // Close on Escape
    item.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        link.setAttribute('aria-expanded', 'false');
        link.focus();
      }
    });
  });
}

/* -----------------------------------------------------------------------------
   5. Predictive Search Modal & Client
   ----------------------------------------------------------------------------- */
/* -----------------------------------------------------------------------------
   5. Predictive Search Modal & Client (Enhanced OS 2.0)
   ----------------------------------------------------------------------------- */
function initPredictiveSearch() {
  const modal = document.getElementById('header-search-modal');
  const trigger = document.querySelector('[data-modal-trigger="search"]');
  if (!modal || !trigger) return;

  const closeBtns = modal.querySelectorAll('[data-close-search]');
  const form = modal.querySelector('form');
  const input = document.getElementById('header-search-input');
  const clearBtn = modal.querySelector('[data-search-clear]');
  const resultsContainer = modal.querySelector('[data-predictive-results]');
  const statusRegion = document.getElementById('predictive-search-status');

  const enableRecent = modal.dataset.enableRecentSearches === 'true';
  const limit = parseInt(modal.dataset.predictiveLimit, 10) || 4;
  const showVendor = modal.dataset.showVendor === 'true';
  const showPrice = modal.dataset.showPrice !== 'false';
  const searchBaseUrl = modal.dataset.searchUrl || '/search';
  const moneyFormat = modal.dataset.moneyFormat || '${{amount}}';

  const RECENT_KEY = 'theme_recent_searches';
  const MAX_RECENTS = 5;

  let debounceTimer = null;
  let abortController = null;
  let activeIndex = -1;
  let lastActiveElement = null;

  // LocalStorage Safe Wrapper for Recent Searches
  function getRecentSearches() {
    try {
      const stored = localStorage.getItem(RECENT_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  function addRecentSearch(query) {
    const q = query.trim();
    if (!q || q.length < 2) return;
    try {
      let list = getRecentSearches();
      list = [q, ...list.filter((item) => item.toLowerCase() !== q.toLowerCase())].slice(0, MAX_RECENTS);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch {}
  }

  function clearAllRecentSearches() {
    try {
      localStorage.removeItem(RECENT_KEY);
      renderRecentSearches();
    } catch {}
  }

  function renderRecentSearches() {
    if (!enableRecent || !resultsContainer) return;
    const recents = getRecentSearches();
    if (recents.length === 0) {
      resultsContainer.innerHTML = '';
      return;
    }

    let html = `
      <div class="predictive-search-recent" data-recent-searches-box>
        <div class="predictive-search-recent__header">
          <span class="predictive-search-heading">Recent Searches</span>
          <button type="button" class="predictive-search-recent__clear-btn" data-clear-recents>Clear all</button>
        </div>
        <ul class="predictive-search-recent__list">
    `;

    recents.forEach((query) => {
      html += `
        <li>
          <button type="button" class="predictive-search-recent__chip" data-recent-query="${escapeHtml(query)}">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span>${escapeHtml(query)}</span>
          </button>
        </li>
      `;
    });

    html += `</ul></div>`;
    resultsContainer.innerHTML = html;
    activeIndex = -1;
  }

  // Currency Formatter
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    let formatString = format || '${{amount}}';
    formatString = formatString.replace(/<[^>]+>/g, '').trim();

    if (isNaN(cents) || cents == null) return '0';
    const amountVal = (cents / 100.0).toFixed(2);
    const parts = amountVal.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    const centsPart = '.' + parts[1];

    return formatString.replace(placeholderRegex, dollars + centsPart);
  }

  function openSearch() {
    lastActiveElement = document.activeElement;
    modal.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    input?.setAttribute('aria-expanded', 'true');
    document.documentElement.setAttribute('scroll-lock', '');
    document.body.style.overflow = 'hidden';

    if (input) {
      setTimeout(() => input.focus(), 50);
      if (input.value.trim().length === 0 && enableRecent) {
        renderRecentSearches();
      }
    }

    document.addEventListener('keydown', handleKeydown);
  }

  function closeSearch() {
    modal.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    input?.setAttribute('aria-expanded', 'false');
    input?.removeAttribute('aria-activedescendant');
    document.documentElement.removeAttribute('scroll-lock');
    document.body.style.overflow = '';

    activeIndex = -1;
    if (lastActiveElement && typeof lastActiveElement.focus === 'function') {
      lastActiveElement.focus();
    } else {
      trigger.focus();
    }
    document.removeEventListener('keydown', handleKeydown);
  }

  // Keyboard navigation & Dialog Trap
  function handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeSearch();
      return;
    }

    // Tab trap inside modal
    if (event.key === 'Tab') {
      const focusables = modal.querySelectorAll('button:not([hidden]), [href], input:not([hidden]), select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
      return;
    }

    // Arrow navigation through suggestions
    const items = resultsContainer.querySelectorAll('.predictive-search-item, .predictive-search-view-all, .predictive-search-recent__chip');
    if (items.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      activeIndex = (activeIndex + 1) % items.length;
      updateActiveItem(items);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      activeIndex = activeIndex - 1;
      if (activeIndex < 0) {
        activeIndex = -1;
        clearActiveItems(items);
        input.focus();
        input.removeAttribute('aria-activedescendant');
      } else {
        updateActiveItem(items);
      }
    } else if (event.key === 'Enter') {
      if (activeIndex >= 0 && items[activeIndex]) {
        event.preventDefault();
        items[activeIndex].click();
      }
    }
  }

  function updateActiveItem(items) {
    items.forEach((item, idx) => {
      if (idx === activeIndex) {
        item.classList.add('is-active');
        item.scrollIntoView({ block: 'nearest' });
        if (item.id) {
          input.setAttribute('aria-activedescendant', item.id);
        }
      } else {
        item.classList.remove('is-active');
      }
    });
  }

  function clearActiveItems(items) {
    items.forEach((item) => item.classList.remove('is-active'));
  }

  trigger.addEventListener('click', openSearch);
  closeBtns.forEach((btn) => btn.addEventListener('click', closeSearch));

  // Form submit saves query to recent searches
  if (form) {
    form.addEventListener('submit', () => {
      const q = input?.value.trim();
      if (enableRecent && q) {
        addRecentSearch(q);
      }
    });
  }

  // Clear button
  if (clearBtn && input) {
    clearBtn.addEventListener('click', () => {
      input.value = '';
      clearBtn.hidden = true;
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
      if (statusRegion) statusRegion.textContent = '';
      if (enableRecent) {
        renderRecentSearches();
      } else if (resultsContainer) {
        resultsContainer.innerHTML = '';
      }
      input.focus();
    });
  }

  // Input listener
  if (input && resultsContainer) {
    input.addEventListener('input', () => {
      const query = input.value.trim();
      activeIndex = -1;

      if (clearBtn) {
        clearBtn.hidden = query.length === 0;
      }

      if (query.length < 2) {
        if (query.length === 0 && enableRecent) {
          renderRecentSearches();
        } else {
          resultsContainer.innerHTML = '';
        }
        if (statusRegion) statusRegion.textContent = '';
        return;
      }

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        fetchPredictiveResults(query);
      }, 250);
    });

    // Delegate clicks in results (e.g. recent chips or clear all)
    resultsContainer.addEventListener('click', (e) => {
      const clearRecentBtn = e.target.closest('[data-clear-recents]');
      if (clearRecentBtn) {
        e.preventDefault();
        clearAllRecentSearches();
        return;
      }

      const recentChip = e.target.closest('[data-recent-query]');
      if (recentChip) {
        e.preventDefault();
        const query = recentChip.dataset.recentQuery;
        input.value = query;
        if (clearBtn) clearBtn.hidden = false;
        fetchPredictiveResults(query);
      }
    });
  }

  function fetchPredictiveResults(query) {
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();

    resultsContainer.innerHTML = `
      <div class="predictive-search-loading" role="status">
        <div class="predictive-search-spinner" aria-hidden="true"></div>
        <span>Searching catalog...</span>
      </div>
    `;
    if (statusRegion) statusRegion.textContent = 'Searching catalog...';

    const root = window.Shopify?.routes?.root || '/';
    const endpoint = `${root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article,query&resources[limit]=${limit}&resources[options][unavailable_products]=last`;

    fetch(endpoint, { signal: abortController.signal })
      .then((res) => {
        if (!res.ok) throw new Error('Search failed');
        return res.json();
      })
      .then((data) => {
        renderPredictiveResults(data, query);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        resultsContainer.innerHTML = `
          <div class="predictive-search-empty predictive-search-empty--error" role="status">
            <p class="predictive-search-empty__heading">Unable to load suggestions</p>
            <p class="predictive-search-empty__subtext">Press Enter to view full results</p>
          </div>
        `;
        if (statusRegion) statusRegion.textContent = 'Search failed. Press enter to search.';
      });
  }

  function renderPredictiveResults(data, query) {
    const results = data.resources?.results;
    if (!results) {
      resultsContainer.innerHTML = '';
      if (input) input.setAttribute('aria-expanded', 'false');
      return;
    }

    const queries = results.queries || [];
    const products = results.products || [];
    const collections = results.collections || [];
    const pages = results.pages || [];
    const articles = results.articles || [];

    const totalCount = queries.length + products.length + collections.length + pages.length + articles.length;

    if (statusRegion) {
      statusRegion.textContent = `${totalCount} results found for ${query}`;
    }

    if (totalCount === 0) {
      resultsContainer.innerHTML = `
        <div class="predictive-search-empty" role="status">
          <div class="predictive-search-empty__icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
              <circle cx="11" cy="11" r="7"/>
              <path d="m20 20-3.5-3.5"/>
              <line x1="8" y1="11" x2="14" y2="11"/>
            </svg>
          </div>
          <p class="predictive-search-empty__heading">No results found for &ldquo;${escapeHtml(query)}&rdquo;</p>
          <p class="predictive-search-empty__subtext">Check your spelling or try different, more general keywords.</p>
        </div>
      `;
      if (input) input.setAttribute('aria-expanded', 'true');
      return;
    }

    let html = '';
    let itemCounter = 0;

    // 1. Query Suggestions
    if (queries.length > 0) {
      html += `
        <div class="predictive-search-group">
          <div class="predictive-search-heading">Suggestions</div>
          <ul class="predictive-search-list">
      `;
      queries.forEach((q) => {
        itemCounter++;
        html += `
          <li>
            <a href="${escapeHtml(q.url)}" class="predictive-search-item" id="pred-item-${itemCounter}" role="option">
              <span class="predictive-search-item__icon" aria-hidden="true">
                <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="8.5" cy="8.5" r="5.5"/>
                  <path d="m13 13 4 4"/>
                </svg>
              </span>
              <div class="predictive-search-info">
                <span class="predictive-search-title">${q.styled_text || escapeHtml(q.text)}</span>
              </div>
              <span class="predictive-search-arrow" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 3l5 5-5 5"/>
                </svg>
              </span>
            </a>
          </li>
        `;
      });
      html += `</ul></div>`;
    }

    // 2. Products
    if (products.length > 0) {
      html += `
        <div class="predictive-search-group">
          <div class="predictive-search-heading">Products</div>
          <ul class="predictive-search-list">
      `;
      products.forEach((p) => {
        itemCounter++;
        const imageSrc = p.image || p.featured_image?.url || '';
        let priceHtml = '';
        if (showPrice && p.price) {
          const formattedPrice = formatMoney(p.price, moneyFormat);
          if (p.compare_at_price_min && p.compare_at_price_min > p.price_min) {
            const comparePrice = formatMoney(p.compare_at_price_min, moneyFormat);
            priceHtml = `
              <div class="predictive-search-price-wrap">
                <span class="predictive-search-price">${formattedPrice}</span>
                <span class="predictive-search-price--compare">${comparePrice}</span>
              </div>
            `;
          } else {
            priceHtml = `
              <div class="predictive-search-price-wrap">
                <span class="predictive-search-price">${formattedPrice}</span>
              </div>
            `;
          }
        }

        const vendorHtml = showVendor && p.vendor ? `<span class="predictive-search-vendor">${escapeHtml(p.vendor)}</span>` : '';
        const isSoldOut = p.available === false;
        const isOnSale = !isSoldOut && p.compare_at_price_min && p.compare_at_price_min > p.price_min;

        let badgeHtml = '';
        if (isSoldOut) {
          badgeHtml = `<span class="predictive-search-badge predictive-search-badge--soldout">Sold out</span>`;
        } else if (isOnSale) {
          badgeHtml = `<span class="predictive-search-badge predictive-search-badge--sale">Sale</span>`;
        }

        const thumbHtml = imageSrc
          ? `<img src="${escapeHtml(imageSrc)}" alt="${escapeHtml(p.title)}" class="predictive-search-thumb" width="56" height="56" loading="lazy">`
          : `<div class="predictive-search-thumb predictive-search-thumb--placeholder" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <path d="m21 15-5-5L5 21"/>
              </svg>
            </div>`;

        html += `
          <li>
            <a href="${escapeHtml(p.url)}" class="predictive-search-item predictive-search-item--product" id="pred-item-${itemCounter}" role="option">
              <div class="predictive-search-thumb-wrap">
                ${thumbHtml}
              </div>
              <div class="predictive-search-info">
                ${vendorHtml}
                <span class="predictive-search-title">${escapeHtml(p.title)}</span>
                <div class="predictive-search-meta">
                  ${priceHtml}
                  ${badgeHtml}
                </div>
              </div>
              <span class="predictive-search-arrow" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 3l5 5-5 5"/>
                </svg>
              </span>
            </a>
          </li>
        `;
      });
      html += `</ul></div>`;
    }

    // 3. Collections
    if (collections.length > 0) {
      html += `
        <div class="predictive-search-group">
          <div class="predictive-search-heading">Collections</div>
          <ul class="predictive-search-list">
      `;
      collections.forEach((c) => {
        itemCounter++;
        html += `
          <li>
            <a href="${escapeHtml(c.url)}" class="predictive-search-item" id="pred-item-${itemCounter}" role="option">
              <span class="predictive-search-item__icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                  <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-6l-2-2H5a2 2 0 0 0-2 2z"/>
                </svg>
              </span>
              <div class="predictive-search-info">
                <span class="predictive-search-title">${escapeHtml(c.title)}</span>
              </div>
              <span class="predictive-search-badge">Collection</span>
              <span class="predictive-search-arrow" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 3l5 5-5 5"/>
                </svg>
              </span>
            </a>
          </li>
        `;
      });
      html += `</ul></div>`;
    }

    // 4. Pages & Articles
    if (pages.length > 0 || articles.length > 0) {
      html += `
        <div class="predictive-search-group">
          <div class="predictive-search-heading">Pages & Articles</div>
          <ul class="predictive-search-list">
      `;
      pages.forEach((page) => {
        itemCounter++;
        html += `
          <li>
            <a href="${escapeHtml(page.url)}" class="predictive-search-item" id="pred-item-${itemCounter}" role="option">
              <span class="predictive-search-item__icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
              </span>
              <div class="predictive-search-info">
                <span class="predictive-search-title">${escapeHtml(page.title)}</span>
              </div>
              <span class="predictive-search-badge">Page</span>
              <span class="predictive-search-arrow" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 3l5 5-5 5"/>
                </svg>
              </span>
            </a>
          </li>
        `;
      });
      articles.forEach((art) => {
        itemCounter++;
        html += `
          <li>
            <a href="${escapeHtml(art.url)}" class="predictive-search-item" id="pred-item-${itemCounter}" role="option">
              <span class="predictive-search-item__icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                  <path d="M19 20H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h10l6 6v8a2 2 0 0 1-2 2z"/>
                  <polyline points="14 4 14 10 20 10"/>
                </svg>
              </span>
              <div class="predictive-search-info">
                <span class="predictive-search-title">${escapeHtml(art.title)}</span>
              </div>
              <span class="predictive-search-badge">Article</span>
              <span class="predictive-search-arrow" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 3l5 5-5 5"/>
                </svg>
              </span>
            </a>
          </li>
        `;
      });
      html += `</ul></div>`;
    }

    // 5. View All Button
    const viewAllUrl = `${searchBaseUrl}?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last`;
    html += `
      <div class="predictive-search-footer">
        <a href="${escapeHtml(viewAllUrl)}" class="predictive-search-view-all" id="pred-item-view-all">
          <span>View all results for &ldquo;${escapeHtml(query)}&rdquo;</span>
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M6 3l5 5-5 5"/>
          </svg>
        </a>
      </div>
    `;

    resultsContainer.innerHTML = html;
    if (input) input.setAttribute('aria-expanded', 'true');
    activeIndex = -1;
  }

  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

/* -----------------------------------------------------------------------------
   6. Cart Indicator Progressive Updates
   ----------------------------------------------------------------------------- */
function initCartListener() {
  const badge = document.getElementById('header-cart-count');
  if (!badge) return;

  function updateBadge(count) {
    const numericCount = parseInt(count, 10) || 0;
    badge.textContent = numericCount;
    badge.dataset.cartCount = numericCount;

    if (numericCount > 0) {
      badge.classList.remove('header__cart-count--empty');
      badge.setAttribute('aria-hidden', 'false');
      badge.classList.add('header__cart-count--pulse');
      setTimeout(() => badge.classList.remove('header__cart-count--pulse'), 300);
    } else {
      badge.classList.add('header__cart-count--empty');
      badge.setAttribute('aria-hidden', 'true');
    }
  }

  // Global helper for other components or future cart drawers
  window.updateHeaderCartCount = updateBadge;

  // Listen to standard custom cart events
  ['cart:updated', 'cart:refresh', 'cart:change'].forEach((eventName) => {
    document.addEventListener(eventName, (e) => {
      if (e.detail?.cart?.item_count !== undefined) {
        updateBadge(e.detail.cart.item_count);
      } else if (e.detail?.item_count !== undefined) {
        updateBadge(e.detail.item_count);
      } else {
        // Fallback: fetch current cart state
        fetch('/cart.js')
          .then((r) => r.json())
          .then((cart) => updateBadge(cart.item_count))
          .catch(() => {});
      }
    });
  });

  // Intercept fetch calls to cart mutation endpoints
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';

    if (
      url.includes('/cart/add') ||
      url.includes('/cart/change') ||
      url.includes('/cart/clear') ||
      url.includes('/cart/update')
    ) {
      // Clone response to read json without consuming it for callers
      response
        .clone()
        .json()
        .then((data) => {
          if (data.item_count !== undefined) {
            updateBadge(data.item_count);
          } else {
            fetch('/cart.js')
              .then((r) => r.json())
              .then((cart) => updateBadge(cart.item_count))
              .catch(() => {});
          }
        })
        .catch(() => {});
    }

    return response;
  };
}
